import type { CrudSelection, MainSelection } from '@/atoms'
import type Feature from 'ol/Feature'
import type Geometry from 'ol/geom/Geometry'
import type { MapSelectionMode } from '../../../atoms/map/map'

import OlMap from 'ol/Map'

import { setAllCrudSelection, setAllMainSelection } from '@/atoms'

import { getMapSelectionContext } from '../../../atoms/selection/read/getMapSelectionContext'
import { selectDepotFeatureInExtent } from './utils/selectDepotFeatureInExtent'
import { selectDeviceFeatureInExtent } from './utils/selectDeviceFeatureInExtent'
import { selectOrderFeatureInExtent } from './utils/selectOrderFeatureInExtent'
import { selectPlaceFeatureInExtent } from './utils/selectPlaceFeatureInExtent'
import { selectRegionFeatureInExtent } from './utils/selectRegionFeatureInExtent'
import { selectRoadSegmentFeatureInExtent } from './utils/selectRoadSegmentFeatureInExtent'
import { selectTrafficAreaFeatureInExtent } from './utils/selectTrafficAreaFeatureInExtent'
import { selectVehicleFeatureInExtent } from './utils/selectVehicleFeatureInExtent'

export const createLassoSelection = async (
  map: OlMap,
  lassoFeature: Feature<Geometry>,
  mode: MapSelectionMode,
  disabledLayers: Set<string>,
) => {
  const lassoPolygon = lassoFeature.getGeometry()
  const lassoExtent = lassoPolygon?.getExtent()

  if (lassoPolygon && lassoExtent) {
    const selection: Partial<MainSelection & CrudSelection> = {
      breadcrumbs: [],
      devices: [],
      depots: [],
      drivers: [],
      orderSteps: [],
      places: [],
      regions: [],
      routes: [],
      unifiedVehicles: [],
    }

    selection.unifiedVehicles = disabledLayers.has('vehicle')
      ? []
      : await selectVehicleFeatureInExtent(map, lassoPolygon, lassoExtent)

    selection.orderSteps = disabledLayers.has('order')
      ? []
      : await selectOrderFeatureInExtent(map, lassoPolygon, lassoExtent)

    selection.devices = disabledLayers.has('device')
      ? []
      : await selectDeviceFeatureInExtent(map, lassoPolygon, lassoExtent)

    selection.depots = disabledLayers.has('depot')
      ? []
      : await selectDepotFeatureInExtent(map, lassoPolygon, lassoExtent)

    selection.places = disabledLayers.has('place')
      ? []
      : await selectPlaceFeatureInExtent(map, lassoPolygon, lassoExtent)

    const mapSelectionContext = getMapSelectionContext()

    if (mapSelectionContext.type === 'crud') {
      selection.regions = disabledLayers.has('region')
        ? []
        : await selectRegionFeatureInExtent(map, lassoPolygon, lassoExtent)

      selection.routingSegments = disabledLayers.has('roadSegment')
        ? []
        : await selectRoadSegmentFeatureInExtent(map, lassoPolygon, lassoExtent)

      selection.trafficArea = []
      if (!disabledLayers.has('trafficArea')) {
        const { trafficAreas, trafficProfiles } = await selectTrafficAreaFeatureInExtent(
          map,
          lassoPolygon,
          lassoExtent,
        )
        selection.trafficProfile = trafficProfiles
        selection.trafficArea = trafficAreas
      }
    }

    switch (mode) {
      case 'lasso': {
        switch (mapSelectionContext.type) {
          case 'main':
            setAllMainSelection(selection, { force: true })
            break
          case 'crud':
            setAllCrudSelection(selection, { force: true })
            break
          case 'none':
          default:
            break
        }
      }

      case 'lassoAdditive':
        switch (mapSelectionContext.type) {
          case 'main':
            setAllMainSelection(selection, { modifiers: { metaKey: true } })
            break
          case 'crud':
            setAllCrudSelection(selection, { modifiers: { metaKey: true } })
            break
          case 'none':
          default:
            break
        }
        break

      case 'lassoSubtractive':
        switch (mapSelectionContext.type) {
          case 'main':
            setAllMainSelection(selection, { modifiers: { altKey: true } })
            break
          case 'crud':
            setAllCrudSelection(selection, { modifiers: { altKey: true } })
            break
          case 'none':
          default:
            break
        }
        break
    }
  }
}
